import React, { useState, useCallback, useEffect } from "react";
import { parseApiUtc } from '@/utils/apiDate';
import { appDate } from '@/utils/appDateFormat';
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import {
  AssetRequestView,
  AssetRequestStatus,
  AssetRequestPriority,
  AssetRequestListQuery,
  ReviewAssetRequestInput,
  getAssetRequests,
  reviewAssetRequest,
  getAssetSettings,
} from "../../../../api/General/assets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import Pagination from "@/components/Pagination";
import FulfillAssetRequestModal from "./FulfillAssetRequestModal";

const statusStyles: Record<AssetRequestStatus, string> = {
  [AssetRequestStatus.Pending]: "bg-yellow-100 text-yellow-800",
  [AssetRequestStatus.Approved]: "bg-green-100 text-green-800",
  [AssetRequestStatus.Rejected]: "bg-red-100 text-red-800",
  [AssetRequestStatus.Fulfilled]: "bg-purple-100 text-purple-800",
};

const statusI18nKeys: Record<AssetRequestStatus, string> = {
  [AssetRequestStatus.Pending]: "assetRequests.pending",
  [AssetRequestStatus.Approved]: "assetRequests.approved",
  [AssetRequestStatus.Rejected]: "assetRequests.rejected",
  [AssetRequestStatus.Fulfilled]: "assetRequests.fulfilled",
};

const priorityStyles: Record<AssetRequestPriority, string> = {
  [AssetRequestPriority.Low]: "bg-blue-100 text-blue-800",
  [AssetRequestPriority.Medium]: "bg-orange-100 text-orange-800",
  [AssetRequestPriority.High]: "bg-red-100 text-red-800",
};

const priorityI18nKeys: Record<AssetRequestPriority, string> = {
  [AssetRequestPriority.Low]: "assetRequests.low",
  [AssetRequestPriority.Medium]: "assetRequests.medium",
  [AssetRequestPriority.High]: "assetRequests.high",
};

export default function AssetRequestsTab() {
  const { t } = useTranslation();

  const [requests, setRequests] = useState<AssetRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<AssetRequestStatus | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<AssetRequestPriority | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Review modal
  const [reviewingRequest, setReviewingRequest] = useState<AssetRequestView | null>(null);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">("approve");
  const [reviewComment, setReviewComment] = useState("");
  const [reviewing, setReviewing] = useState(false);

  // Fulfill modal
  const [fulfillRequest, setFulfillRequest] = useState<AssetRequestView | null>(null);
  const [fulfillOpen, setFulfillOpen] = useState(false);

  // OTP setting
  const [requireOtp, setRequireOtp] = useState(true);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load OTP setting once on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await getAssetSettings();
        if (r.errors.length === 0) setRequireOtp(r.payload.requireAssetIssuanceOtp);
      } catch {
        // keep default (true = require OTP)
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const query: AssetRequestListQuery = {
        search: debouncedSearch || undefined,
        status: statusFilter,
        priority: priorityFilter,
        page,
        pageSize,
      };
      const result = await getAssetRequests(query);
      if (result.errors.length === 0) {
        setRequests(result.payload.items);
        setTotalCount(result.payload.totalCount);
        setTotalPages(result.payload.totalPages);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, priorityFilter, page, pageSize, reloadNonce]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Refresh action in the search bar: clear search + dropdowns and force a reload
  const handleReset = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter(undefined);
    setPriorityFilter(undefined);
    setPage(1);
    setReloadNonce((n) => n + 1);
  }, []);

  const handleReview = useCallback(async () => {
    if (!reviewingRequest) return;
    if (reviewAction === "reject" && !reviewComment.trim()) {
      showToast(t("assetRequests.commentRequiredForReject"), "error");
      return;
    }

    setReviewing(true);
    try {
      const input: ReviewAssetRequestInput = {
        requestId: reviewingRequest.id,
        status: reviewAction === "approve" ? AssetRequestStatus.Approved : AssetRequestStatus.Rejected,
        comment: reviewComment || undefined,
      };
      const result = await reviewAssetRequest(input);
      if (result.errors.length === 0) {
        showToast(
          reviewAction === "approve"
            ? t("assetRequests.requestApproved")
            : t("assetRequests.requestRejected"),
          "success"
        );
        setReviewingRequest(null);
        setReviewComment("");
        loadRequests();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setReviewing(false);
    }
  }, [reviewingRequest, reviewAction, reviewComment, loadRequests, showToast, t]);

  const openReview = (req: AssetRequestView, action: "approve" | "reject") => {
    setReviewingRequest(req);
    setReviewAction(action);
    setReviewComment("");
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("assetRequests.searchPlaceholder")}
            className="w-full ps-10 pe-11 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            data-testid="request-search"
          />
          <button
            type="button"
            onClick={handleReset}
            title={t("assets.resetFilters")}
            aria-label={t("assets.resetFilters")}
            className="absolute end-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            data-testid="request-filters-reset"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <select
          value={statusFilter ?? ""}
          onChange={(e) => { setStatusFilter(e.target.value ? e.target.value as AssetRequestStatus : undefined); setPage(1); }}
          className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="request-status-filter"
        >
          <option value="">{t("assetRequests.allStatuses")}</option>
          <option value={AssetRequestStatus.Pending}>{t("assetRequests.pending")}</option>
          <option value={AssetRequestStatus.Approved}>{t("assetRequests.approved")}</option>
          <option value={AssetRequestStatus.Rejected}>{t("assetRequests.rejected")}</option>
          <option value={AssetRequestStatus.Fulfilled}>{t("assetRequests.fulfilled")}</option>
        </select>
        <select
          value={priorityFilter ?? ""}
          onChange={(e) => { setPriorityFilter(e.target.value ? e.target.value as AssetRequestPriority : undefined); setPage(1); }}
          className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="request-priority-filter"
        >
          <option value="">{t("assetRequests.allPriorities")}</option>
          <option value={AssetRequestPriority.Low}>{t("assetRequests.low")}</option>
          <option value={AssetRequestPriority.Medium}>{t("assetRequests.medium")}</option>
          <option value={AssetRequestPriority.High}>{t("assetRequests.high")}</option>
        </select>
      </div>

      {/* Request Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetRequests.noRequests")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetRequests.noRequestsDescription")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Card key={req.id} variant="elevated" data-testid={`request-card-${req.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground">{req.categoryName}</h4>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[req.status]}`}>
                        {t(statusI18nKeys[req.status])}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyles[req.priority]}`}>
                        {t(priorityI18nKeys[req.priority])}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t("assetRequests.requestedBy")}:</span>{" "}
                        <span className="text-foreground font-medium">{req.employeeName}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t("assetRequests.requestDate")}:</span>{" "}
                        <span className="text-foreground">{appDate(parseApiUtc(req.createdOn))}</span>
                      </div>
                      {req.department && (
                        <div>
                          <span className="text-muted-foreground">{t("assetRequests.department")}:</span>{" "}
                          <span className="text-foreground">{req.department}</span>
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{req.reason}</p>
                    {req.reviewComment && (
                      <div className="mt-2 bg-muted rounded-md p-2">
                        <p className="text-xs text-muted-foreground">{t("assetRequests.reviewComment")} ({req.reviewedByName}):</p>
                        <p className="text-sm text-foreground">{req.reviewComment}</p>
                      </div>
                    )}
                  </div>
                  {req.status === AssetRequestStatus.Pending && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="success"
                        size="xs"
                        onClick={() => openReview(req, "approve")}
                        data-testid={`approve-btn-${req.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t("assetRequests.approve")}
                      </Button>
                      <Button
                        variant="destructive"
                        size="xs"
                        onClick={() => openReview(req, "reject")}
                        data-testid={`reject-btn-${req.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t("assetRequests.reject")}
                      </Button>
                    </div>
                  )}
                  {req.status === AssetRequestStatus.Approved && (
                    <div className="shrink-0">
                      <Button
                        size="xs"
                        onClick={() => { setFulfillRequest(req); setFulfillOpen(true); }}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid={`fulfill-btn-${req.id}`}
                      >
                        <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        {t("assetRequests.fulfill")}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          variant="compact"
          pageSize={pageSize}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          totalCount={totalCount}
        />
      )}

      {/* Review Modal */}
      <Dialog open={!!reviewingRequest} onOpenChange={(o) => { if (!o) { setReviewingRequest(null); setReviewComment(""); }}}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === "approve" ? t("assetRequests.approveRequest") : t("assetRequests.rejectRequest")}
            </DialogTitle>
            <DialogDescription>
              {reviewingRequest?.categoryName} — {reviewingRequest?.employeeName}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="bg-muted rounded-lg p-3 text-sm">
              <p className="text-muted-foreground">{t("assetRequests.reason")}:</p>
              <p className="text-foreground mt-1">{reviewingRequest?.reason}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t("assetRequests.comment")} {reviewAction === "reject" && <span className="text-destructive">*</span>}
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={
                  reviewAction === "approve"
                    ? t("assetRequests.approveCommentPlaceholder")
                    : t("assetRequests.rejectCommentPlaceholder")
                }
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                data-testid="review-comment"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setReviewingRequest(null); setReviewComment(""); }}
              data-testid="review-cancel-btn"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant={reviewAction === "approve" ? "success" : "destructive"}
              onClick={handleReview}
              loading={reviewing}
              data-testid="review-submit-btn"
            >
              {reviewing ? t("assetRequests.processing") : (reviewAction === "approve" ? t("assetRequests.approve") : t("assetRequests.reject"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fulfill Modal */}
      <FulfillAssetRequestModal
        open={fulfillOpen}
        onClose={() => { setFulfillOpen(false); setFulfillRequest(null); }}
        request={fulfillRequest}
        onFulfilled={() => {
          showToast(t("assetRequests.requestFulfilled"), "success");
          loadRequests();
        }}
        requireOtp={requireOtp}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 end-6 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-success/10 text-success border border-success/20" : "bg-destructive/10 text-destructive border border-destructive/20"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
